import { type Editor, type JSONContent } from '@tiptap/core';
import { type Node as ProseMirrorNode } from '@tiptap/pm/model';

import {
  filterWorkspaceAgentComposerSelectionDragItemsToAuthorizedSnapshot,
  parseWorkspaceAgentComposerSelectionDragPayload,
  WORKSPACE_AGENT_COMPOSER_SELECTION_DROP_TYPE,
} from '~/components/v2/components/workspace/workspace-agent-panel/drag/workspaceAgentComposerSelectionDragPayload';
import { getWorkspaceAgentComposerSelectionDragAuthorizedItemKeys } from '~/components/v2/components/workspace/workspace-agent-panel/drag/workspaceAgentComposerSelectionDragSession';
import {
  findWorkspaceAgentPillIdByReference,
  type WorkspaceAgentPillReference,
} from '~/components/v2/components/workspace/workspace-agent-panel/tiptap/findWorkspaceAgentPillIdByReference';
import {
  WORKSPACE_AGENT_PILL_NODE_NAME,
  type WorkspaceAgentPillNodeAttrs,
} from '~/components/v2/components/workspace/workspace-agent-panel/tiptap/workspaceAgentPill';
import { resolveWorkspaceAgentComposerSelectionDragItem } from '~/components/v2/components/workspace/workspace-agent-panel/utils/parseWorkspaceAgentComposerReferenceUrl';

export type WorkspaceAgentComposerSelectionDropDataTransfer = Pick<DataTransfer, 'getData'>;

const pillReferenceFromAttrs = (attrs: WorkspaceAgentPillNodeAttrs): WorkspaceAgentPillReference | null => {
  if (attrs.type === 'board' && attrs.boardId) {
    return { boardId: attrs.boardId, type: 'board' };
  }

  if (attrs.type === 'asset' && attrs.assetId) {
    return { assetId: attrs.assetId, type: 'asset', versionId: attrs.versionId };
  }

  return null;
};

const buildPillContentFromResolvedAttrs = (
  editor: Editor,
  resolvedAttrsList: readonly WorkspaceAgentPillNodeAttrs[],
): JSONContent[] => {
  const content: JSONContent[] = [];

  for (const resolvedAttrs of resolvedAttrsList) {
    const pillReference = pillReferenceFromAttrs(resolvedAttrs);
    const existingPillId =
      pillReference === null ? null : findWorkspaceAgentPillIdByReference(editor.state.doc, pillReference);
    const attrs: WorkspaceAgentPillNodeAttrs = existingPillId
      ? { ...resolvedAttrs, id: existingPillId }
      : resolvedAttrs;

    content.push({ attrs, type: WORKSPACE_AGENT_PILL_NODE_NAME }, { text: ' ', type: 'text' });
  }

  return content;
};

export const insertWorkspaceAgentComposerSelectionFromDrop = async ({
  dataTransfer,
  docAtDrop,
  editor,
  hadActiveDragSession,
  sessionIdAtDrop,
  shouldCompleteInsert,
  workspaceId,
}: {
  dataTransfer: WorkspaceAgentComposerSelectionDropDataTransfer;
  docAtDrop: ProseMirrorNode;
  editor: Editor;
  hadActiveDragSession: boolean;
  sessionIdAtDrop: number;
  shouldCompleteInsert: () => boolean;
  workspaceId: string;
}): Promise<boolean> => {
  if (!hadActiveDragSession || !workspaceId || editor.isDestroyed) {
    return false;
  }

  const rawPayload = dataTransfer.getData(WORKSPACE_AGENT_COMPOSER_SELECTION_DROP_TYPE);
  const payload = parseWorkspaceAgentComposerSelectionDragPayload(rawPayload);

  if (!payload || payload.items.length === 0 || payload.sessionId !== sessionIdAtDrop) {
    return false;
  }

  const authorizedItems = filterWorkspaceAgentComposerSelectionDragItemsToAuthorizedSnapshot(
    payload.items,
    getWorkspaceAgentComposerSelectionDragAuthorizedItemKeys(),
  );

  if (authorizedItems.length === 0) {
    return false;
  }

  const resolvedAttrsList = (
    await Promise.all(
      authorizedItems.map((item) => resolveWorkspaceAgentComposerSelectionDragItem({ item, workspaceId })),
    )
  ).filter((attrs): attrs is WorkspaceAgentPillNodeAttrs => attrs !== null);

  if (
    resolvedAttrsList.length === 0 ||
    editor.isDestroyed ||
    !shouldCompleteInsert() ||
    !editor.state.doc.eq(docAtDrop)
  ) {
    return false;
  }

  const content = buildPillContentFromResolvedAttrs(editor, resolvedAttrsList);

  if (content.length === 0) {
    return false;
  }

  if (!shouldCompleteInsert() || !editor.state.doc.eq(docAtDrop) || editor.isDestroyed) {
    return false;
  }

  // Drag-over blurs the editor; `editor.view.focus()` avoids TipTap's mismatched-transaction error.
  editor.view.focus();

  const didInsert = editor.chain().focus('end').insertContent(content).scrollIntoView().run();

  const selection = window.getSelection();

  if (selection && selection.rangeCount > 0) {
    selection.collapseToEnd();
  }

  return didInsert;
};
